/* eslint-disable jira/filename-case */
import React, { type ReactNode } from 'react';
// eslint-disable-next-line jira/restricted/redux
import type { Store } from 'redux';
import { Provider as ReduxProvider } from '@atlassian/jira-react-redux/src/index.tsx';
import { JSErrorBoundary } from '@jira/platform__js-error-boundary/src/JSErrorBoundary.tsx';

type Props = {
	children: ReactNode;
	/**
	 * ID for error reporting.
	 * @see {JSErrorBoundary}
	 */
	id?: string;
	/**
	 * Team name for error reporting.
	 * @see {JSErrorBoundary}
	 */
	teamName?: string;
	/**
	 * Package name for error reporting.
	 * @see {JSErrorBoundary}
	 */
	packageName?: string;
	/**
	 * Fallback behaviour when error encountered. Default: 'flag'.
	 * @see {JSErrorBoundary}
	 */
	fallback?: 'flag' | 'page';
	/**
	 * Redux store to add a provider for. Default: none.
	 * @see {Store}
	 * @see {ReduxProvider}
	 */
	store?: Store<unknown>;
};

/**
 * A boundary around an "app," where an app is a distinct piece of functionality, and could potentially be nested within another app.
 *
 * Wraps children in an error boundary (by default) and a redux store provider (if store is passed).
 */
export function AppBase({
	// FIXME: id should be required
	id = '@jira/platform__utils-app-base::unknown-app',
	// FIXME: packageName should be required
	packageName = '@jira/platform__utils-app-base',
	teamName,
	// JSErrorBoundary fallback default is 'page' but AppBase is used around any "app"
	fallback = 'flag',
	store,
	children,
}: Props): React.JSX.Element {
	let contents: ReactNode = children;

	if (store) {
		contents = <ReduxProvider store={store}>{contents}</ReduxProvider>;
	}

	return (
		<JSErrorBoundary id={id} teamName={teamName} fallback={fallback} packageName={packageName}>
			{contents}
		</JSErrorBoundary>
	);
}

export default AppBase;
